import { categories, resources } from "@/mocks/resources";
import type {
  CategoryKey,
  CategoryMeta,
  FilterState,
  Resource,
  SortKey,
} from "@/types/content";

export const PAGE_SIZE = 6;

export const REGIONS = ["中国", "日本", "韩国", "美国"];
export const YEARS = ["2024", "2023", "2022", "2021"];

export const TYPE_OPTIONS: Record<CategoryKey, string[]> = {
  movie: ["电影", "电视剧", "短片", "纪录片"],
  anime: ["TV动画", "剧场版", "国漫"],
  music: ["专辑", "现场实录", "原声带", "单曲"],
};

export const TAG_OPTIONS: Record<CategoryKey, string[]> = {
  movie: ["科幻", "悬疑", "剧情", "犯罪", "惊悚", "纪录", "年代", "都市"],
  anime: ["科幻", "冒险", "青春", "武侠", "治愈", "奇幻", "剧情"],
  music: ["电子", "独立", "民谣", "城市流行", "后摇", "爵士", "世界音乐", "氛围"],
};

export const EMPTY_FILTER: FilterState = {
  type: "",
  region: "",
  year: "",
  tag: "",
};

export function getCategoryMeta(key: string): CategoryMeta | undefined {
  return categories.find((item) => item.key === key);
}

export function getResourceById(id: string): Resource | undefined {
  return resources.find((item) => item.id === id);
}

export function getCategoryResources(key: CategoryKey): Resource[] {
  return resources.filter((item) => item.category === key);
}

export interface FilterOptions {
  types: string[];
  regions: string[];
  years: string[];
  tags: string[];
}

export function getFilterOptions(key: CategoryKey): FilterOptions {
  const list = getCategoryResources(key);
  const tags = Array.from(new Set(list.flatMap((item) => item.tags)));
  return {
    types: TYPE_OPTIONS[key],
    regions: REGIONS.filter((region) => list.some((item) => item.region === region)),
    years: YEARS.filter((year) => list.some((item) => item.year === year)),
    tags,
  };
}

export function matchFilter(resource: Resource, filter: FilterState): boolean {
  if (filter.type && resource.type !== filter.type) return false;
  if (filter.region && resource.region !== filter.region) return false;
  if (filter.year && resource.year !== filter.year) return false;
  if (filter.tag && !resource.tags.includes(filter.tag)) return false;
  return true;
}

export function filterResources(list: Resource[], filter: FilterState): Resource[] {
  return list.filter((item) => matchFilter(item, filter));
}

export function sortResources(list: Resource[], sort: SortKey): Resource[] {
  const next = [...list];
  if (sort === "views") {
    return next.sort((a, b) => b.views - a.views);
  }
  if (sort === "rating") {
    return next.sort((a, b) => b.rating - a.rating);
  }
  return next.sort((a, b) => b.publishDate.localeCompare(a.publishDate));
}

export function paginate<T>(list: T[], page: number, size: number = PAGE_SIZE): T[] {
  const start = (page - 1) * size;
  return list.slice(start, start + size);
}

export function totalPages(count: number, size: number = PAGE_SIZE): number {
  return Math.max(1, Math.ceil(count / size));
}

export function searchResources(keyword: string): Resource[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return resources.filter((item) => {
    const haystack = [item.title, item.summary, item.description, item.type, item.region, ...item.tags]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

export function getRelatedResources(target: Resource, limit: number = 4): Resource[] {
  const sameCategory = resources.filter(
    (item) => item.id !== target.id && item.category === target.category,
  );
  const scored = sameCategory.map((item) => {
    const sharedTags = item.tags.filter((tag) => target.tags.includes(tag)).length;
    return { item, score: sharedTags * 10 + item.rating };
  });
  scored.sort((a, b) => b.score - a.score);
  if (scored.length >= limit) return scored.slice(0, limit).map((entry) => entry.item);
  const fallback = resources.filter(
    (item) => item.id !== target.id && item.category !== target.category,
  );
  return [...scored.map((entry) => entry.item), ...fallback].slice(0, limit);
}

export function getHotResources(limit: number = 8): Resource[] {
  return [...resources].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)} 万`;
  }
  return `${views}`;
}